// Предел пирамиды растровых тайлов.
//
// Пирамиды слоёв данных нарезаются до z6 (src/tiles/generator.py: MAX_ZOOM = 6).
// Шесть, а не семь: поля лежат на 1/12° ≈ 9 км, и на z6 это уже пять пикселей на
// ячейку данных — седьмой уровень был такой же интерполяцией, какую MapLibre
// делает сам растяжением последнего уровня, но стоил 71 % всех тайлов.
// Сервер на отсутствующий тайл отдаёт не 404, а ПРОЗРАЧНЫЙ PNG — чтобы карта не
// сыпала ошибками. Из-за этого выход за предел выглядит не как сбой, а как
// «фон не подгрузился»: слой на месте, тайлы приходят, все пустые.
//
// Промахнуться легко: MapLibre считает базовым тайл 512 px, поэтому для наших
// 256-пиксельных тайлов он запрашивает зум НА ЕДИНИЦУ БОЛЬШЕ зума карты
// (sourceTileZoom). На zoom 6.4 карта просила z7 и всё рисовалось, а на 6.75 —
// уже z8, которого нет: фон пропадал при одном движении колеса.
//
// Лечится объявлением maxzoom у источника: MapLibre тогда РАСТЯГИВАЕТ тайлы
// последнего уровня вместо запроса несуществующих.

/** До какого зума нарезаются пирамиды (совпадает с MAX_ZOOM генератора). */
export const MAX_PYRAMID_ZOOM = 6

/** Докуда сервер отдаёт подложку СНИМКА (`src/api/tiles.py: IMAGERY_MAX_ZOOM`).
 *
 *  Подложка рисуется лениво, как батиметрия, но предел у неё ЕСТЬ — и объявить
 *  его обязательно. Пиксель снимка 10 м, потолок сервера 16; попросив z17,
 *  MapLibre получил бы 400 на каждый тайл, то есть пропажу картинки в одно
 *  движение колеса — ровно то, чем болел общий потолок 12 до 05.09.2026.
 *  С объявленным пределом MapLibre РАСТЯГИВАЕТ последний уровень.
 *
 *  Число совпадает с серверным, и совпадение держит сторож
 *  `tests/imagery/test_imagery_tile_zoom.py`: разъехавшись, две копии молчат. */
export const IMAGERY_MAX_ZOOM = 16

/** Зум тайла, который MapLibre запросит у источника с тайлами `tileSize` px. */
export function sourceTileZoom(mapZoom: number, tileSize = 256): number {
  return Math.round(mapZoom + Math.log2(512 / tileSize))
}

/** Предел зума для источника по шаблону URL.
 *
 *  `undefined` — у слоя предела нет: батиметрия, облачность, туман и апвеллинг
 *  рисуются ЛЕНИВО на любой запрошенный зум, ограничивать их значило бы мылить
 *  картинку на приближении. Ограничиваем только заранее нарезанные пирамиды —
 *  shared-поля и вероятность (HSI). */
export function rasterMaxZoom(urlTemplate: string): number | undefined {
  if (urlTemplate.includes('/raster/imagery/')) return IMAGERY_MAX_ZOOM
  const isPyramid =
    urlTemplate.includes('/raster/shared/') || urlTemplate.includes('/probability_')
  return isPyramid ? MAX_PYRAMID_ZOOM : undefined
}

/** Спецификация растрового источника для `map.addSource`.
 *
 *  КЛЮЧЕВОЕ: `maxzoom` попадает в объект ТОЛЬКО когда у слоя есть предел пирамиды.
 *  MapLibre валидирует стиль и падает на `maxzoom: undefined`
 *  («number expected, undefined found») — источник не создаётся, а слой затем не
 *  находит source. Из-за этого молча пропадали ленивые оверлеи (облачность,
 *  туман): у них `rasterMaxZoom` = undefined, и явный ключ ломал addSource.
 *  Поэтому строим объект условно — ключа быть не должно, а не быть с undefined. */
export function rasterSourceSpec(urlTemplate: string): {
  type: 'raster'
  tiles: string[]
  tileSize: number
  maxzoom?: number
} {
  const maxzoom = rasterMaxZoom(urlTemplate)
  return {
    type: 'raster',
    tiles: [urlTemplate],
    tileSize: 256,
    ...(maxzoom !== undefined ? { maxzoom } : {}),
  }
}
